import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Keyboard, LogOut, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useAuth } from "@/app/auth/context";

import { SHORTCUTS_EVENT } from "./ShortcutsDialog";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "")).toUpperCase() || "?";
}

const ITEM =
  "flex h-9 cursor-default items-center gap-2.5 rounded-control px-2.5 text-sm outline-none select-none data-[highlighted]:bg-sunken";

export function UserMenu() {
  const { t } = useTranslation();
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t("user.menu")}
        className="flex items-center gap-2.5 rounded-full py-1 pr-2 pl-1 text-sm hover:bg-sunken"
      >
        <span className="grid size-8 place-items-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
          {initials(me.user.display_name)}
        </span>
        <span className="hidden max-w-40 truncate font-medium sm:inline">{me.user.display_name}</span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 min-w-52 rounded-card border border-line bg-surface p-1.5 shadow-pop"
        >
          <DropdownMenu.Label className="px-2.5 py-1.5">
            <span className="block text-sm font-medium">{me.user.display_name}</span>
            <span className="block text-xs text-ink-subtle">{me.user.username}</span>
          </DropdownMenu.Label>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item className={ITEM} onSelect={() => void navigate("/profile")}>
            <UserRound className="size-4 text-ink-muted" aria-hidden="true" />
            {t("user.profile")}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={ITEM}
            onSelect={() => window.dispatchEvent(new Event(SHORTCUTS_EVENT))}
          >
            <Keyboard className="size-4 text-ink-muted" aria-hidden="true" />
            {t("shortcuts.title")}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={ITEM}
            onSelect={() => {
              void signOut().then(() => navigate("/login"));
            }}
          >
            <LogOut className="size-4 text-ink-muted" aria-hidden="true" />
            {t("auth.signOut")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
